import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

/* Pointer-driven interactions. Every one of these writes through GSAP quickTo,
   never through React state, so a moving cursor never re-renders the tree. */

export const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isFinePointer = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine)').matches;

/**
 * Magnetic hover. The element leans toward the cursor while it is nearby and
 * springs back on leave. Returns a ref to attach to the element.
 */
export const useMagnetic = ({ strength = 0.34, radius = 90 } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced() || !isFinePointer()) return undefined;

    const xTo = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' });

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      xTo(dx * strength);
      yTo(dy * strength);
    };
    const onLeave = () => { xTo(0); yTo(0); };

    // Listen on a padded area so the pull starts before the cursor arrives.
    const onDocMove = (e) => {
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX > r.left - radius && e.clientX < r.right + radius &&
        e.clientY > r.top - radius && e.clientY < r.bottom + radius;
      if (inside) onMove(e); else onLeave();
    };

    window.addEventListener('pointermove', onDocMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onDocMove);
      gsap.killTweensOf(el);
      gsap.set(el, { x: 0, y: 0 });
    };
  }, [strength, radius]);

  return ref;
};

/**
 * A soft light that trails the pointer. Not a cursor replacement: the real
 * cursor is untouched, this only lights the surface underneath it.
 */
export const usePointerLight = (scopeRef) => {
  const light = useRef(null);

  useEffect(() => {
    const el = light.current;
    const scope = scopeRef.current;
    if (!el || !scope || prefersReduced() || !isFinePointer()) return undefined;

    gsap.set(el, { xPercent: -50, yPercent: -50, opacity: 0 });
    const xTo = gsap.quickTo(el, 'x', { duration: 0.85, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.85, ease: 'power3.out' });

    const onMove = (e) => {
      const r = scope.getBoundingClientRect();
      xTo(e.clientX - r.left);
      yTo(e.clientY - r.top);
    };
    const onEnter = () => gsap.to(el, { opacity: 1, duration: 0.5 });
    const onLeave = () => gsap.to(el, { opacity: 0, duration: 0.5 });

    scope.addEventListener('pointermove', onMove, { passive: true });
    scope.addEventListener('pointerenter', onEnter);
    scope.addEventListener('pointerleave', onLeave);
    return () => {
      scope.removeEventListener('pointermove', onMove);
      scope.removeEventListener('pointerenter', onEnter);
      scope.removeEventListener('pointerleave', onLeave);
      gsap.killTweensOf(el);
    };
  }, [scopeRef]);

  return light;
};

const GLYPHS = '#$%&/\\<>*+-_[]{}0123456789';

/**
 * Scramble-decode text once, when it scrolls into view. Preserves spaces so
 * the word shapes stay readable while it resolves.
 */
export const useScramble = (ref, { speed = 26, hold = 3 } = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced()) return undefined;

    const final = el.textContent;
    let frame = 0;
    let raf = 0;
    let started = false;

    const run = () => {
      const done = Math.floor(frame / hold);
      let out = '';
      for (let i = 0; i < final.length; i += 1) {
        const ch = final[i];
        if (ch === ' ') { out += ' '; continue; }
        out += i < done ? ch : GLYPHS[(Math.random() * GLYPHS.length) | 0];
      }
      el.textContent = out;
      frame += 1;
      if (done <= final.length) raf = requestAnimationFrame(run);
      else el.textContent = final;
    };

    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting || started) return;
        started = true;
        io.disconnect();
        setTimeout(() => { raf = requestAnimationFrame(run); }, 60);
      },
      { threshold: 0.4 }
    );
    io.observe(el);

    return () => { io.disconnect(); cancelAnimationFrame(raf); el.textContent = final; };
  }, [ref, speed, hold]);
};
